import { useEffect } from 'react';
import { atom } from 'nanostores';
import { useStore } from '@nanostores/react';
import { activeRouteStore } from '@customerio/hydra-nav-state';
import { getWorkspaceIdFromCurrentUrl } from '@/utils/workspace-id';
import { authStore } from '@/services/auth/store';

// The copilot sidebar is a global panel with no route of its own, so it inherits the host page's
// URL. On workspace-less pages (e.g. /settings/ai, account home) the URL has no workspace, which
// used to make it send an empty environment_id and break every workspace-scoped call. Track the
// last resolved workspace so the sidebar keeps acting on the workspace the user is working in.
// The dedicated /workspaces/:id/agent page always has the workspace in its URL and is unaffected.
const lastKnownWorkspaceId = atom<number | null>(null);

export function rememberCopilotWorkspaceId(id: number | null): void {
  if (id != null) {
    lastKnownWorkspaceId.set(id);
  }
}

// Prefer the workspace in the current URL (authoritative on workspace pages and the dedicated
// agent page); fall back to the last workspace the sidebar saw, then the user's last workspace.
export function resolveCopilotWorkspaceId(): number | null {
  const fromUrl = getWorkspaceIdFromCurrentUrl();
  if (fromUrl != null) {
    return fromUrl;
  }

  const remembered = lastKnownWorkspaceId.get();
  if (remembered != null) {
    return remembered;
  }

  // Number('') and Number('0') are both 0, so guard on a positive id rather than NaN.
  const parsed = Number(authStore.get()?.user?.last_cio_workspace_id);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
}

// Remember the workspace the user is on, re-reading on every (cross-app) route change. Only an
// authoritative URL-derived workspace is remembered — a workspace-less page yields null and is
// ignored, so it never overwrites the last real workspace the user was working in.
export function useTrackCopilotWorkspace(): void {
  useStore(activeRouteStore); // re-render on route changes so the URL below is re-read
  const workspaceId = getWorkspaceIdFromCurrentUrl();
  useEffect(() => {
    rememberCopilotWorkspaceId(workspaceId);
  }, [workspaceId]);
}
